import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Search, ArrowUpDown, ArrowDown, ArrowUp, Trophy, Euro, ListChecks,
  Building2, Plane, ChevronRight,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api } from "@/lib/api";

const fmtEUR = (n) => (n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
const TIPO_LABEL = {
  alojamiento: "Alojamiento", actividad: "Actividad", transfer: "Transfer",
  tren: "Tren", vuelo: "Vuelo", entradas: "Entradas", free_day: "Día libre", otro: "Otro",
};
const MEDAL = ["text-amber-500", "text-slate-400", "text-orange-700"];

const Kpi = ({ icon: Icon, label, value, tone }) => (
  <Card><CardContent className="flex items-center gap-3 p-4">
    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}><Icon className="h-5 w-5" /></div>
    <div><p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-xl font-bold tabular-nums">{value}</p></div>
  </CardContent></Card>
);

const SortHead = ({ children, col, sort, setSort, className }) => {
  const active = sort.key === col;
  const Icon = !active ? ArrowUpDown : sort.dir === "desc" ? ArrowDown : ArrowUp;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => setSort((s) => ({ key: col, dir: s.key === col && s.dir === "desc" ? "asc" : "desc" }))}
        className={`inline-flex items-center gap-1 hover:text-foreground ${active ? "text-foreground" : "text-muted-foreground"}`}
        data-testid={`ranking-sort-${col}`}
      >
        {children} <Icon className="h-3.5 w-3.5" />
      </button>
    </TableHead>
  );
};

export const RankingTab = () => {
  const [term, setTerm] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [sort, setSort] = useState({ key: "gasto", dir: "desc" });
  const [selected, setSelected] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["bi-ranking-proveedores", desde, hasta],
    queryFn: async () => (await api.get("/bi/gasto-proveedores", {
      params: { desde: desde || undefined, hasta: hasta || undefined },
    })).data,
  });

  const tot = data?.totales || { gasto: 0, servicios: 0, proveedores: 0, tipos: 0 };
  const all = data?.por_proveedor || [];
  const maxGasto = Math.max(1, ...all.map((p) => p.gasto));

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase();
    const filtered = t ? all.filter((p) => p.proveedor.toLowerCase().includes(t)) : all;
    const dir = sort.dir === "desc" ? -1 : 1;
    return [...filtered].sort((a, b) => {
      if (sort.key === "proveedor") return a.proveedor.localeCompare(b.proveedor) * dir;
      return (a[sort.key] - b[sort.key]) * dir;
    });
  }, [all, term, sort]);

  // ranking absoluto por gasto (independiente del filtro/orden de la tabla)
  const rankMap = useMemo(() => {
    const m = {};
    [...all].sort((a, b) => b.gasto - a.gasto).forEach((p, i) => { m[p.proveedor] = i + 1; });
    return m;
  }, [all]);

  return (
    <div className="space-y-4" data-testid="ranking-tab">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div className="relative sm:max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar proveedor…" className="pl-8" data-testid="ranking-search" />
        </div>
        <div className="flex items-end gap-2">
          <div className="space-y-1"><Label className="text-[11px] uppercase text-muted-foreground">Desde</Label>
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-40" data-testid="ranking-desde" /></div>
          <div className="space-y-1"><Label className="text-[11px] uppercase text-muted-foreground">Hasta</Label>
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-40" data-testid="ranking-hasta" /></div>
        </div>
      </div>

      {isLoading ? <Skeleton className="h-24 w-full" /> : (
        <div className="grid grid-cols-3 gap-3">
          <Kpi icon={Euro} label="Gasto total" value={fmtEUR(tot.gasto)} tone="bg-sky-100 text-sky-700" />
          <Kpi icon={ListChecks} label="Servicios" value={tot.servicios} tone="bg-emerald-100 text-emerald-700" />
          <Kpi icon={Building2} label="Proveedores" value={tot.proveedores} tone="bg-violet-100 text-violet-700" />
        </div>
      )}

      <Card><CardContent className="p-0">
        <div className="flex items-center gap-2 border-b px-4 py-3 text-sm font-semibold">
          <Trophy className="h-4 w-4 text-amber-500" /> Ranking de gasto por proveedor
        </div>
        {isLoading ? (
          <div className="space-y-2 p-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-14 text-center">#</TableHead>
              <SortHead col="proveedor" sort={sort} setSort={setSort}>Proveedor</SortHead>
              <SortHead col="servicios" sort={sort} setSort={setSort} className="w-24 text-center">Serv.</SortHead>
              <SortHead col="gasto" sort={sort} setSort={setSort} className="w-44 text-right">Gasto</SortHead>
              <TableHead className="w-10" />
            </TableRow></TableHeader>
            <TableBody data-testid="ranking-body">
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">Sin proveedores que coincidan.</TableCell></TableRow>
              ) : rows.map((p) => {
                const rank = rankMap[p.proveedor];
                return (
                  <TableRow
                    key={p.proveedor}
                    className="cursor-pointer"
                    onClick={() => setSelected(p)}
                    data-testid={`ranking-row-${rank}`}
                  >
                    <TableCell className="text-center">
                      <span className={`inline-flex items-center gap-1 font-bold tabular-nums ${rank <= 3 ? MEDAL[rank - 1] : "text-muted-foreground"}`}>
                        {rank <= 3 && <Trophy className="h-3.5 w-3.5" />}{rank}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{p.proveedor}</div>
                      <div className="mt-1 h-1.5 rounded-full bg-sky-500" style={{ width: `${(p.gasto / maxGasto) * 100}%` }} />
                    </TableCell>
                    <TableCell className="text-center tabular-nums">{p.servicios}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{fmtEUR(p.gasto)}</TableCell>
                    <TableCell className="text-right"><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      <ProveedorDetalleSheet
        proveedor={selected}
        rank={selected ? rankMap[selected.proveedor] : null}
        desde={desde}
        hasta={hasta}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};

const ProveedorDetalleSheet = ({ proveedor, rank, desde, hasta, onClose }) => {
  const open = !!proveedor;
  const { data, isLoading } = useQuery({
    queryKey: ["bi-proveedor-detalle", proveedor?.proveedor, desde, hasta],
    enabled: open,
    queryFn: async () => (await api.get("/bi/proveedor-detalle", {
      params: { proveedor: proveedor.proveedor, desde: desde || undefined, hasta: hasta || undefined },
    })).data,
  });
  const items = data?.items || [];
  const tipos = data?.por_tipo || [];
  const tot = data?.totales || { gasto: 0, servicios: 0 };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="ranking-detalle-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {rank && <Badge variant="secondary" className="tabular-nums">#{rank}</Badge>}
            {proveedor?.proveedor}
          </SheetTitle>
          <SheetDescription>Detalle de servicios contratados a este proveedor.</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <Kpi icon={Euro} label="Gasto" value={fmtEUR(tot.gasto)} tone="bg-sky-100 text-sky-700" />
            <Kpi icon={ListChecks} label="Servicios" value={tot.servicios} tone="bg-emerald-100 text-emerald-700" />
          </div>

          {tipos.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {tipos.map((t) => (
                <Badge key={t.tipo} variant="outline" className="font-normal" data-testid={`detalle-tipo-${t.tipo}`}>
                  {TIPO_LABEL[t.tipo] || t.tipo}: <span className="ml-1 font-semibold tabular-nums">{fmtEUR(t.gasto)}</span>
                </Badge>
              ))}
            </div>
          )}

          <Card><CardContent className="p-0">
            {isLoading ? (
              <div className="space-y-2 p-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Servicio</TableHead>
                  <TableHead className="w-24">Fecha</TableHead>
                  <TableHead className="w-32 text-right">Gasto</TableHead>
                </TableRow></TableHeader>
                <TableBody data-testid="ranking-detalle-body">
                  {items.length === 0 ? (
                    <TableRow><TableCell colSpan={3} className="py-8 text-center text-sm text-muted-foreground">Sin servicios.</TableCell></TableRow>
                  ) : items.map((it, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <div className="font-medium leading-tight">{it.service}</div>
                        <div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                          <Plane className="h-3 w-3" /> {it.viaje}{it.destino ? ` · ${it.destino}` : ""}
                          <Badge variant="secondary" className="ml-1 text-[10px] font-normal">{TIPO_LABEL[it.tipo] || it.tipo}</Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground tabular-nums">{it.fecha || "—"}</TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">{fmtEUR(it.gasto)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </div>
      </SheetContent>
    </Sheet>
  );
};
